import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Button from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { paddleService } from '../../services/paddleService';
import { PLANS } from '../../config/plans';

const PlansContainer = styled.div`
  padding: ${theme.spacing.xxxl};
  max-width: 900px;
  margin: 0 auto;
  font-family: ${theme.typography.fontFamily};
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xxl};
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSizes.xxxl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSizes.base};
  color: ${theme.colors.text};
  margin: 0;
  opacity: 0.8;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.xl};
  margin-top: ${theme.spacing.xl};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${props => props.$isCurrent ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  display: flex;
  flex-direction: column;
  transition: border-color 0.2s ease;
  opacity: ${props => props.$isDisabled ? 0.6 : 1};

  &:hover {
    border-color: ${props => props.$isDisabled ? theme.colors.border : theme.colors.primary};
  }
`;

const CurrentBadge = styled.div`
  background: ${theme.colors.primary};
  color: white;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSizes.xs};
  font-weight: ${theme.typography.fontWeights.bold};
  text-transform: uppercase;
  display: inline-block;
  margin-bottom: ${theme.spacing.sm};
`;

const PlanName = styled.h2`
  font-size: ${theme.typography.fontSizes.xl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const PlanDescription = styled.p`
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  opacity: 0.8;
`;

const PlanPrice = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Price = styled.div`
  font-size: ${theme.typography.fontSizes.xxxl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.text};

  span {
    font-size: ${theme.typography.fontSizes.base};
    color: ${theme.colors.text};
    font-weight: ${theme.typography.fontWeights.normal};
    opacity: 0.7;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${theme.spacing.xl} 0;
  flex: 1;
`;

const Feature = styled.li`
  padding: ${theme.spacing.xs} 0;
  color: ${theme.colors.text};
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSizes.sm};
  line-height: 1.5;

  &:before {
    content: '✓';
    color: ${theme.colors.success};
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const StatusInfo = styled.div`
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
`;

const StatusText = styled.p`
  margin: 0;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};

  strong {
    color: ${theme.colors.primary};
  }
`;

const ConfigWarning = styled.div`
  background: rgba(255, 184, 108, 0.1);
  border: 1px solid ${theme.colors.warning};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
`;

const DowngradeNote = styled.div`
  text-align: center;
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.sm};
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  opacity: 0.8;
`;

const ContactLink = styled.a`
  color: ${theme.colors.primary};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SubscriptionPlans = ({ onPlanSelected }) => {
  const { user, subscription, refreshSubscription } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paddleReady, setPaddleReady] = useState(false);

  useEffect(() => {
    paddleService
      .initialize()
      .then(() => setPaddleReady(true))
      .catch((err) => {
        console.error('Failed to initialize Paddle:', err);
      });

    const handleCheckoutCompleted = async () => {
      setLoading(false);
      setSelectedPlan(null);

      setTimeout(async () => {
        if (refreshSubscription) {
          await refreshSubscription();
        }
        if (onPlanSelected) {
          onPlanSelected();
        }
      }, 2000);
    };

    const handleCheckoutClosed = () => {
      setLoading(false);
      setSelectedPlan(null);
    };

    window.addEventListener('paddle-checkout-completed', handleCheckoutCompleted);
    window.addEventListener('paddle-checkout-closed', handleCheckoutClosed);

    return () => {
      window.removeEventListener('paddle-checkout-completed', handleCheckoutCompleted);
      window.removeEventListener('paddle-checkout-closed', handleCheckoutClosed);
    };
  }, [onPlanSelected, refreshSubscription]);

  const handleSelectPlan = async (planId) => {
    if (!user) {
      alert('Please sign up or log in first');
      return;
    }

    if (!paddleService.isConfigured()) {
      alert('Payment system is not configured. Please contact support.');
      return;
    }

    setLoading(true);
    setSelectedPlan(planId);

    try {
      // If user has existing subscription, update it instead of creating new
      if (subscription?.paddleSubscriptionId) {
        await paddleService.updateSubscription(subscription.paddleSubscriptionId, planId, user);
        // Refresh after API update
        setTimeout(async () => {
          if (refreshSubscription) {
            await refreshSubscription();
          }
          setLoading(false);
          setSelectedPlan(null);
          if (onPlanSelected) {
            onPlanSelected();
          }
        }, 1000);
      } else {
        // No existing subscription, create new checkout
        await paddleService.openCheckout(planId, user);
      }
    } catch (error) {
      console.error('Error with subscription:', error);
      alert('Failed to process subscription: ' + error.message);
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const isCurrentPlan = (planId) => {
    return subscription && subscription.tier === planId && subscription.status === 'active';
  };

  const getTrialDaysRemaining = () => {
    if (!subscription || subscription.status !== 'trial' || !subscription.trialEndDate) {
      return 0;
    }

    const now = new Date();
    const endDate = new Date(subscription.trialEndDate);
    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, daysRemaining);
  };

  const trialDaysRemaining = getTrialDaysRemaining();
  const plans = Object.values(PLANS);
  const currentTier = subscription?.tier;
  const isOnStarter = currentTier === 'starter' && subscription?.status === 'active';
  const isOnPro = currentTier === 'pro' && subscription?.status === 'active';

  const getButtonText = (planId) => {
    if (isCurrentPlan(planId)) {
      return 'Current Plan';
    }
    if (isOnStarter && planId === 'pro') {
      return 'Upgrade to Pro';
    }
    if (isOnPro && planId === 'starter') {
      return 'Contact to Downgrade';
    }
    return 'Select Plan';
  };

  const isButtonDisabled = (planId) => {
    if (isCurrentPlan(planId)) return true;
    if (isOnPro && planId === 'starter') return true; // Can't self-service downgrade
    if (loading) return true;
    if (!paddleReady) return true;
    return false;
  };

  const handleButtonClick = (planId) => {
    if (isOnPro && planId === 'starter') {
      // Downgrade requires support
      window.location.href = 'mailto:support@splitstep.ai?subject=Downgrade%20Request&body=I%20would%20like%20to%20downgrade%20from%20Pro%20to%20Starter.';
      return;
    }
    handleSelectPlan(planId);
  };

  return (
    <PlansContainer>
      <Header>
        <Title>
          {isOnStarter ? 'Upgrade Your Plan' : isOnPro ? 'Your Plan' : 'Choose Your Plan'}
        </Title>
        <Subtitle>
          {isOnStarter
            ? 'Upgrade to Pro for unlimited labels'
            : isOnPro
              ? 'You\'re on our best plan'
              : 'Select the plan that fits your needs'}
        </Subtitle>
      </Header>

      {!paddleService.isConfigured() && (
        <ConfigWarning>
          Payment system not configured. Contact support if this persists.
        </ConfigWarning>
      )}

      {subscription && subscription.status === 'trial' && trialDaysRemaining > 0 && (
        <StatusInfo>
          <StatusText>
            You have <strong>{trialDaysRemaining} days</strong> remaining in your free trial
          </StatusText>
        </StatusInfo>
      )}

      {subscription && subscription.status === 'expired' && (
        <StatusInfo>
          <StatusText>
            Your trial has ended. <strong>Choose a plan</strong> to continue using the service.
          </StatusText>
        </StatusInfo>
      )}

      <PlansGrid>
        {plans.map((plan) => {
          const isCurrent = isCurrentPlan(plan.id);
          const isDowngrade = isOnPro && plan.id === 'starter';

          return (
            <PlanCard key={plan.id} $isCurrent={isCurrent} $isDisabled={isDowngrade}>
              {isCurrent && <CurrentBadge>Current Plan</CurrentBadge>}
              <PlanName>{plan.name}</PlanName>
              <PlanDescription>{plan.description}</PlanDescription>
              <PlanPrice>
                <Price>
                  ${plan.price}
                  <span> / {plan.period}</span>
                </Price>
              </PlanPrice>
              <FeatureList>
                {plan.features.map((feature, index) => (
                  <Feature key={index}>{feature}</Feature>
                ))}
              </FeatureList>
              <Button
                onClick={() => handleButtonClick(plan.id)}
                disabled={isButtonDisabled(plan.id)}
                variant={isCurrent || isDowngrade ? 'ghost' : 'default'}
                style={{
                  width: '100%',
                  cursor: isCurrent ? 'default' : undefined,
                }}
              >
                {loading && selectedPlan === plan.id ? 'Loading...' : getButtonText(plan.id)}
              </Button>
            </PlanCard>
          );
        })}
      </PlansGrid>

      {isOnPro && (
        <DowngradeNote>
          To downgrade to Starter, please contact{' '}
          <ContactLink href="mailto:support@splitstep.ai">support@splitstep.ai</ContactLink>
        </DowngradeNote>
      )}
    </PlansContainer>
  );
};

export default SubscriptionPlans;
